import { z } from 'zod';
import { Role } from './roles.ts';

// STE Connect contract (Phase 5). Site-scoped calls go to /api/v1/sites/{siteId}/… and are
// relayed to the gateway with the same paths as the local API.

export const ApiInfo = z.object({
  mode: z.enum(['gateway', 'cloud']),
  siteId: z.string().optional(),
});
export type ApiInfo = z.infer<typeof ApiInfo>;

export const CloudSite = z.object({
  id: z.string(),
  title: z.string(),
  role: Role,
  online: z.boolean(),
  deviceId: z.string().optional(),
  /** STE-2026-0001 (ADR-0016) */
  serial: z.string().optional(),
  lastSeen: z.string().optional(),
  /** STE staff: push notifications of this site subscribed (ADR-0025) */
  subscribed: z.boolean().optional(),
});
export type CloudSite = z.infer<typeof CloudSite>;

export const CloudAccount = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string(),
  globalRole: z.enum(['installer', 'admin']).nullable(),
  totpEnabled: z.boolean(),
});
export type CloudAccount = z.infer<typeof CloudAccount>;

export const MembershipRole = z.enum(['owner', 'member', 'installer']);
export type MembershipRole = z.infer<typeof MembershipRole>;

export const SiteMember = z.object({
  userId: z.string(),
  email: z.string(),
  name: z.string(),
  role: MembershipRole,
  totpEnabled: z.boolean(),
});
export type SiteMember = z.infer<typeof SiteMember>;

export const InviteRequest = z.object({ email: z.email(), role: MembershipRole });
export const InviteResponse = z.object({
  link: z.string(),
  expiresAt: z.string(),
  /** the invitation went out by e-mail to this address (ADR-0027) */
  emailedTo: z.string().optional(),
});
export type InviteResponse = z.infer<typeof InviteResponse>;

export const InvitationInfo = z.object({
  siteTitle: z.string(),
  email: z.string(),
  role: MembershipRole,
  accountExists: z.boolean(),
});
export type InvitationInfo = z.infer<typeof InvitationInfo>;

export const AcceptInvitationRequest = z.object({
  token: z.string().min(16).max(128),
  name: z.string().min(1).max(100).optional(),
  password: z.string().min(10).max(256).optional(),
});

/** content of the QR code on the device: STE1:<deviceId>:<pairingSecret> */
export const PairingCode = z
  .string()
  .trim()
  .transform((v) => v.replace(/^STE1:/i, ''))
  .pipe(z.string().regex(/^[a-z0-9-]{6,64}:[A-Z2-7]{16,64}$/, 'Ungültiger Kopplungscode'));

export const PairRequest = z.object({ code: z.string().min(10).max(200) });

export const CreateSiteRequest = z.object({ title: z.string().min(1).max(64) });

export function parsePairingCode(code: string): { deviceId: string; secret: string } | undefined {
  const parsed = PairingCode.safeParse(code);
  if (!parsed.success) return undefined;
  const [deviceId, secret] = parsed.data.split(':');
  return deviceId && secret ? { deviceId, secret } : undefined;
}

// ---- STE team (global staff accounts, admins only) ------------------------------

export const GlobalRole = z.enum(['installer', 'admin']);
export type GlobalRole = z.infer<typeof GlobalRole>;

export const StaffMember = CloudAccount.extend({
  globalRole: GlobalRole,
  disabled: z.boolean(),
  lastLoginAt: z.string().nullable(),
  createdAt: z.string(),
});
export type StaffMember = z.infer<typeof StaffMember>;

export const StaffInvitation = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string(),
  globalRole: GlobalRole,
  createdAt: z.string(),
  expiresAt: z.string(),
});
export type StaffInvitation = z.infer<typeof StaffInvitation>;

export const TeamOverview = z.object({
  members: z.array(StaffMember),
  invitations: z.array(StaffInvitation),
});
export type TeamOverview = z.infer<typeof TeamOverview>;

export const StaffInviteRequest = z.object({
  email: z.email().max(254),
  name: z.string().trim().min(1).max(100),
  globalRole: GlobalRole,
});
export type StaffInviteRequest = z.infer<typeof StaffInviteRequest>;

export const UpdateStaffRequest = z
  .object({ disabled: z.boolean().optional(), globalRole: GlobalRole.optional() })
  .refine((v) => v.disabled !== undefined || v.globalRole !== undefined, 'nothing to change');
export type UpdateStaffRequest = z.infer<typeof UpdateStaffRequest>;

export const StaffInvitationInfo = z.object({
  email: z.string(),
  name: z.string(),
  globalRole: GlobalRole,
  expiresAt: z.string(),
});
export type StaffInvitationInfo = z.infer<typeof StaffInvitationInfo>;

export const TotpSetup = z.object({ secret: z.string(), uri: z.string() });
export type TotpSetup = z.infer<typeof TotpSetup>;

export const AcceptStaffInvitationRequest = z.object({
  token: z.string().min(16).max(128),
  name: z.string().trim().min(1).max(100),
  password: z.string().min(10).max(256),
  totp: z.string().regex(/^\d{6}$/),
});
export type AcceptStaffInvitationRequest = z.infer<typeof AcceptStaffInvitationRequest>;

/** mandatory TOTP set up right after the password check (login or invitation) */
export const TotpSetupTokenRequest = z.object({ setupToken: z.string().min(16).max(2048) });
export const TotpSetupConfirmRequest = TotpSetupTokenRequest.extend({ totp: z.string().regex(/^\d{6}$/) });

// ---- service access: the customer opens the installer area with a per-site password ----------

/** how long the installer area stays open after entering the service password */
export const SERVICE_ACCESS_TTL_SECONDS = 60 * 60;

export const ServiceAccessRequest = z.object({ password: z.string().trim().min(1).max(64) });
export type ServiceAccessRequest = z.infer<typeof ServiceAccessRequest>;

/** an access token with installer rights for this one site; the refresh token is unchanged */
export const ServiceAccessResponse = z.object({
  accessToken: z.string(),
  expiresIn: z.number().int().positive(),
});
export type ServiceAccessResponse = z.infer<typeof ServiceAccessResponse>;

export const ServicePassword = z.object({ password: z.string(), updatedAt: z.string() });
export type ServicePassword = z.infer<typeof ServicePassword>;

/** customers type it from a phone call or a note: case, spaces and dashes do not matter */
export function normalizeServicePassword(value: string): string {
  return value.toUpperCase().replace(/[\s-]/g, '');
}

/** without password: generate a new random one */
export const SetServicePasswordRequest = z.object({
  password: z.string().trim().min(8).max(64).optional(),
});

// ---- password reset by link (ADR-0015), sent by e-mail when configured (ADR-0027) -------

export const PasswordResetLinkRequest = z.object({ resetTotp: z.boolean().default(false) });
export type PasswordResetLinkRequest = z.infer<typeof PasswordResetLinkRequest>;

export const PasswordResetLink = z.object({
  link: z.string(),
  expiresAt: z.string(),
  /** the link went out by e-mail to this address (ADR-0027) */
  emailedTo: z.string().optional(),
});
export type PasswordResetLink = z.infer<typeof PasswordResetLink>;

export const PasswordResetInfo = z.object({
  email: z.string(),
  name: z.string(),
  /** the current TOTP code is needed to set the new password */
  totpRequired: z.boolean(),
});
export type PasswordResetInfo = z.infer<typeof PasswordResetInfo>;

/** "Passwort vergessen?" on the login page: the link comes by e-mail only */
export const ForgotPasswordRequest = z.object({ email: z.email().max(254) });
export type ForgotPasswordRequest = z.infer<typeof ForgotPasswordRequest>;

export const CompletePasswordResetRequest = z.object({
  token: z.string().min(16).max(128),
  password: z.string().min(10).max(256),
  totp: z
    .string()
    .regex(/^\d{6}$/)
    .optional(),
});
export type CompletePasswordResetRequest = z.infer<typeof CompletePasswordResetRequest>;

/** device passport of a Pi for STE admins (ADR-0016) */
export const DevicePassport = z.object({
  deviceId: z.string(),
  serial: z.string().nullable(),
  recoveryPassword: z.string().nullable(),
  wifiKey: z.string().nullable(),
});
export type DevicePassport = z.infer<typeof DevicePassport>;
